import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, RefreshCw, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { DeviceType } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  FLEET_PERMISSIONS,
  FLEET_ROUTES,
  PRINT_ERROR_CODES,
  errorCode,
  refusalAffected,
  type DeviceVersionPolicy,
  type FleetDevice,
  type UpsertVersionPolicyBody,
  type VersionCompliance,
} from '../fleetApi';
import { assessDevice } from '../fleetHealth';
import { VERSION_VISUALS } from '../fleetVisuals';
import { apiMessage, formatDateTime, useQueryParams } from '../fleetHelpers';
import PanelResult from './PanelResult';
import VersionPolicyDialog from './VersionPolicyDialog';

const DEVICE_TYPES = Object.values(DeviceType);
const COMPLIANCES: VersionCompliance[] = ['UNSUPPORTED', 'OUTDATED', 'UNKNOWN', 'SUPPORTED'];

/**
 * ## Version compliance, and the unsupported-device warning
 *
 * A version floor is a tenant policy, not a hardcoded constant: one venue runs
 * a fleet it updates weekly and another has a KDS nobody has touched in a year,
 * and a platform-wide minimum would take the second one off the air.
 *
 * **BLOCK is the setting with an outage on the other side of it.** It refuses
 * the *heartbeat* — not PIN login, deliberately, because locking a venue's tills
 * out of the payment flow is a bigger outage than the one it prevents — and the
 * server refuses to save a BLOCK floor that live devices already fail unless
 * `force` is sent. This screen surfaces that refusal as what it is: a count of
 * how many devices would stop working, and a second, explicit confirmation
 * carrying that number. An operator raising the floor should hear it here rather
 * than from the shift.
 *
 * The compliance breakdown beside the policies is derived from the device list,
 * because there is no endpoint that reports it per type. `UNKNOWN` is kept
 * separate from `SUPPORTED` throughout: "we do not know what this is running"
 * and "this is fine" are different answers, and folding the first into the
 * second is how a fleet of unreported devices comes to look fully patched.
 */
export default function VersionPolicyPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();

  const [dialogFor, setDialogFor] = useState<{ policy: DeviceVersionPolicy | null } | null>(null);

  const canManage = can(FLEET_PERMISSIONS.devicesManageVersions);

  const policiesQuery = useApi<DeviceVersionPolicy[]>(FLEET_ROUTES.versionPolicies);
  const devicesQuery = useApi<unknown>(FLEET_ROUTES.devices, useQueryParams({ limit: 200 }));

  const policies = useMemo(() => policiesQuery.data ?? [], [policiesQuery.data]);
  const devices = useMemo(
    () => rowsOf<FleetDevice>(devicesQuery.data).rows,
    [devicesQuery.data],
  );

  const reload = () => {
    policiesQuery.reload();
    devicesQuery.reload();
  };

  /** Compliance counts per device type, over the devices the list returned. */
  const breakdown = useMemo(() => {
    const byType = new Map<DeviceType, Record<VersionCompliance, number>>();
    for (const device of devices) {
      const assessed = assessDevice(device, policies);
      const bucket =
        byType.get(device.type) ??
        ({ SUPPORTED: 0, OUTDATED: 0, UNSUPPORTED: 0, UNKNOWN: 0 } as Record<
          VersionCompliance,
          number
        >);
      bucket[assessed.version.compliance] += 1;
      byType.set(device.type, bucket);
    }
    return byType;
  }, [devices, policies]);

  const rows = useMemo(
    () =>
      DEVICE_TYPES.map((type) => ({
        type,
        policy: policies.find((policy) => policy.deviceType === type) ?? null,
        counts: breakdown.get(type) ?? null,
      })).filter((row) => row.policy !== null || row.counts !== null),
    [policies, breakdown],
  );

  const save = async (body: UpsertVersionPolicyBody): Promise<boolean> => {
    const attempt = async (payload: UpsertVersionPolicyBody) => {
      await api.post(FLEET_ROUTES.versionPolicies, payload);
    };
    try {
      await attempt(body);
      toast.success(t('fleet.versions.saved'));
      reload();
      return true;
    } catch (error) {
      if (errorCode(error) === PRINT_ERROR_CODES.versionPolicyWouldBlock) {
        // The refusal is the point of the whole feature. Say the number out
        // loud, and make the override a second, deliberate act.
        const affected = refusalAffected(error) ?? 0;
        const ok = await confirm.confirm({
          title: t('fleet.versions.wouldBlockTitle'),
          body: t('fleet.versions.wouldBlockBody', {
            count: affected,
            version: body.minSupportedVersion,
          }),
          confirmLabel: t('fleet.versions.wouldBlockConfirm'),
          tone: 'danger',
        });
        if (!ok) return false;
        try {
          await attempt({ ...body, force: true });
          toast.success(t('fleet.versions.saved'));
          reload();
          return true;
        } catch (forced) {
          toast.error(apiMessage(forced, t('fleet.errors.actionFailed')));
          return false;
        }
      }
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
      return false;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-ink-muted">{t('fleet.versions.explainer')}</p>
        <div className="ms-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon as={RefreshCw} />
            {t('fleet.common.refresh')}
          </Button>
          {canManage && (
            <Button onClick={() => setDialogFor({ policy: null })}>
              <Icon as={Plus} />
              {t('fleet.versions.add')}
            </Button>
          )}
        </div>
      </div>

      <PanelResult
        loading={policiesQuery.loading && !policiesQuery.data}
        error={policiesQuery.error}
        count={rows.length}
        onRetry={reload}
        subject={t('fleet.versions.subject')}
        emptyTitle={t('fleet.versions.emptyTitle')}
        emptyHint={t('fleet.versions.emptyHint')}
        emptyAction={
          canManage ? (
            <Button onClick={() => setDialogFor({ policy: null })}>
              <Icon as={Plus} />
              {t('fleet.versions.add')}
            </Button>
          ) : undefined
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('fleet.common.type')}</TH>
                <TH>{t('fleet.versions.minSupported')}</TH>
                <TH>{t('fleet.versions.recommended')}</TH>
                <TH>{t('fleet.versions.enforcement')}</TH>
                <TH>{t('fleet.versions.fleet')}</TH>
                <TH>{t('fleet.versions.updated')}</TH>
                <TH className="text-end">{t('fleet.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {rows.map(({ type, policy, counts }) => {
                const unsupported = counts?.UNSUPPORTED ?? 0;
                return (
                  <TR
                    key={type}
                    className={cn(
                      'border-s-4',
                      unsupported > 0
                        ? 'border-s-red-500 bg-red-50/40 dark:bg-red-950/20'
                        : 'border-s-transparent',
                    )}
                  >
                    <TD>
                      <span className="font-medium">{t(`fleet.deviceType.${type}`)}</span>
                    </TD>
                    <TD className="tabular-nums">
                      {policy?.minSupportedVersion ?? (
                        <span className="text-ink-muted">{t('fleet.versions.noPolicy')}</span>
                      )}
                    </TD>
                    <TD className="tabular-nums">
                      {policy?.recommendedVersion ?? (
                        <span className="text-ink-muted">{t('fleet.common.dash')}</span>
                      )}
                    </TD>
                    <TD>
                      {policy ? (
                        <Badge tone={policy.enforcement === 'BLOCK' ? 'danger' : 'warning'}>
                          {t(`fleet.enforcement.${policy.enforcement}`)}
                        </Badge>
                      ) : (
                        <span className="text-ink-muted">{t('fleet.common.dash')}</span>
                      )}
                    </TD>
                    <TD>
                      {counts ? (
                        <div className="flex flex-wrap gap-1">
                          {COMPLIANCES.filter((compliance) => counts[compliance] > 0).map(
                            (compliance) => (
                              <span
                                key={compliance}
                                className={cn(
                                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                                  VERSION_VISUALS[compliance].severity >= 2
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-slate-100 text-slate-700',
                                )}
                              >
                                <Icon
                                  as={VERSION_VISUALS[compliance].icon}
                                  size="sm"
                                  aria-hidden
                                />
                                {counts[compliance]} {t(`fleet.compliance.${compliance}`)}
                              </span>
                            ),
                          )}
                        </div>
                      ) : (
                        <span className="text-ink-muted">{t('fleet.versions.noDevices')}</span>
                      )}
                    </TD>
                    <TD className="text-ink-muted">
                      {policy ? formatDateTime(policy.updatedAt, i18n.language) : t('fleet.common.dash')}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        {canManage && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={policy ? t('common.edit') : t('fleet.versions.setFloor')}
                            onClick={() => setDialogFor({ policy })}
                          >
                            <Icon as={policy ? Pencil : Plus} />
                            <span className="sr-only">
                              {policy ? t('common.edit') : t('fleet.versions.setFloor')}
                            </span>
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {/* The one warning that is not about a device being offline: the tablet is
          alive and answering, it is simply running a build the tenant will not
          support. Under a BLOCK policy it is already being refused heartbeats. */}
      {rows.some((row) => (row.counts?.UNSUPPORTED ?? 0) > 0) && (
        <div className="flex items-start gap-2 rounded-xl border border-red-300 bg-red-50/60 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
          <Icon as={TriangleAlert} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
          <div>
            <p className="font-medium">{t('fleet.versions.unsupportedTitle')}</p>
            <p className="text-ink-muted">{t('fleet.versions.unsupportedBody')}</p>
          </div>
        </div>
      )}

      {!canManage && (
        <p className="text-xs text-ink-muted">{t('fleet.versions.readOnlyNote')}</p>
      )}

      <VersionPolicyDialog
        open={dialogFor !== null}
        policy={dialogFor?.policy ?? null}
        takenTypes={policies.map((policy) => policy.deviceType)}
        onClose={() => setDialogFor(null)}
        onSave={save}
      />
    </div>
  );
}
